interface MarkdownMediaToken {
    id: string;
    markdown: string;
}

const MARKDOWN_MEDIA_PATTERN = /[!#]\[([^\]\r\n]*)\]\([^\r\n)]*\)/g;

/**
 * Replace custom Markdown image/video markup before sending prose to a
 * translation service. Storage URLs and the trailing size/id metadata are not
 * natural language and translation services can truncate or rewrite them.
 */
export function markdownTextForTranslation(markdown: string): string {
    let index = 0;
    return String(markdown ?? '').replace(MARKDOWN_MEDIA_PATTERN, () =>
        `[[PRISM_MEDIA_${index++}]]`);
}

/**
 * Restore every source image/video token into translated Markdown. This also
 * repairs historical translations where a media token was kept but truncated
 * before its closing parenthesis.
 */
export function restoreMarkdownMediaAfterTranslation(
    sourceMarkdown: string,
    translatedMarkdown: string,
): string {
    const media = markdownMediaTokens(sourceMarkdown);
    if (!media.length) return translatedMarkdown;

    let restored = String(translatedMarkdown ?? '');
    media.forEach((token, index) => {
        const placeholder = new RegExp(
            `\\[\\s*\\[\\s*PRISM[\\s_-]*MEDIA[\\s_-]*${index}\\s*\\]\\s*\\]`,
            'gi',
        );
        restored = restored.replace(placeholder, token.markdown);

        // Some existing translations contain the original media prefix and URL
        // but stop mid-token. Replace either a complete or an incomplete token
        // carrying the same stable media id with the exact source token.
        const escapedId = escapeRegExp(token.id);
        const completeToken = new RegExp(`[!#]\\[${escapedId}\\]\\([^\\r\\n)]*\\)`, 'g');
        restored = restored.replace(completeToken, token.markdown);
        if (!restored.includes(token.markdown)) {
            const incompleteToken = new RegExp(`[!#]\\[${escapedId}\\]\\([^\\r\\n]*(?=\\r?$|\\r?\\n)`, 'gm');
            restored = restored.replace(incompleteToken, token.markdown);
        }
    });

    // A translator may remove a placeholder entirely. Images are more useful at
    // the end of the alert than silently missing, so retain any absent source
    // media in its original order.
    const missing = media.filter(token => !restored.includes(token.markdown));
    if (missing.length) {
        restored = `${restored.trimEnd()}\n\n${missing.map(token => token.markdown).join('\n')}`;
    }
    return restored;
}

function markdownMediaTokens(markdown: string): MarkdownMediaToken[] {
    return [...String(markdown ?? '').matchAll(MARKDOWN_MEDIA_PATTERN)]
        .map(match => ({ id: match[1], markdown: match[0] }));
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
